import React, { useState, useEffect } from 'react';
import { useTransaction } from '../api/getTransaction';
import { Button, FullScreenSpinner } from 'components/Elements';
import { useParams } from 'react-router-dom';
import { Breadcrumbs, ContentLayout } from 'components/Layout';
import { imageToBlob } from 'utils/ImageToBlob';
import { motion } from 'framer-motion';
import { ChevronLeft, Eye, EyeOff, X } from 'lucide-react';
import { decryptRef } from 'utils/encryption';
import { Link } from 'react-router-dom';
import { ExternalLink } from 'lucide-react';

export const Transaction = () => {
  const { encRef } = useParams();
  const txRef = decryptRef(encRef);
  const [showTxRef, setShowTxRef] = useState(false);

  if (!txRef) {
    return <div>Invalid or corrupted reference</div>;
  }

  const transactionQuery = useTransaction({ tx_ref: txRef });
  const [senderPhotoUrl, setSenderPhotoUrl] = useState(null);
  const [showProofModal, setShowProofModal] = useState(false);

  useEffect(() => {
    if (transactionQuery.data?.transactionDetails?.senderPhoto) {
      imageToBlob(transactionQuery.data.transactionDetails.senderPhoto).then(
        (localURL) => {
          if (localURL) {
            setSenderPhotoUrl(localURL);
          }
        }
      );
    }
  }, [transactionQuery.data]);

  if (transactionQuery.isLoading) return <FullScreenSpinner />;
  if (!transactionQuery.data) return null;

  const transaction = transactionQuery.data.transactionDetails;
  const isProofUrl =
    typeof transaction.summary === 'string' &&
    transaction.summary.startsWith('http');

  const statusColor =
    {
      pending: 'bg-yellow-500/10 text-yellow-400',
      successful: 'bg-green-500/10 text-green-400',
      failed: 'bg-red-500/10 text-red-400',
    }[transaction.status] || 'bg-gray-500/10 text-gray-300';

  const proof = imageToBlob(transaction?.summary);

  return (
    <ContentLayout
      title="Transaction Details"
      breadcrumbs={
        <>
          <nav
            className="flex justify-between items-center"
            aria-label="Breadcrumb"
          >
            <ol className="flex items-center space-x-2 text-sm">
              <li className="flex items-center space-x-1">
                <Link
                  to="/app"
                  className="text-white/70 hover:text-white transition px-2 py-1"
                >
                  Overview
                </Link>
                <span className="text-white/40">/</span>
                <Link
                  to="/app/transactions"
                  className="text-white/70 hover:text-white transition px-2 py-1"
                >
                  Transactions
                </Link>
                <span className="text-white/40">/</span>
                <span className="text-white font-semibold bg-white/20 px-3 py-1 rounded-full backdrop-blur-sm">
                  Transaction details
                </span>
              </li>
            </ol>

            <motion.button
              onClick={() => navigate(prevPath)}
              className="flex items-center gap-2 text-sm text-white/70 hover:text-white bg-white/10 px-3 py-1 rounded-full backdrop-blur-md transition shadow-sm"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <motion.div
                animate={{ x: [0, 3, -3, 0] }}
                transition={{
                  duration: 0.6,
                  repeat: Infinity,
                  repeatType: 'loop',
                  ease: 'easeInOut',
                }}
              >
                <ChevronLeft size={14} />
              </motion.div>
              <span>Back</span>
            </motion.button>
          </nav>
        </>
      }
    >
      {/* Sender Info */}
      <div className="mb-8 bg-white/10 dark:bg-white/5 backdrop-blur-md p-6 rounded-2xl shadow border border-white/10">
        <div className="flex items-center gap-5">
          {transaction.senderPhoto ? (
            <img
              src={senderPhotoUrl || transaction.senderPhoto}
              alt={`${transaction.senderFirstName} ${transaction.senderLastName}`}
              className="w-16 h-16 rounded-full object-cover border border-white/20 shadow"
            />
          ) : (
            <div className="w-16 h-16 rounded-full bg-white/20 text-white font-bold flex items-center justify-center text-xl shadow">
              {transaction.senderFirstName?.charAt(0).toUpperCase() || '?'}
            </div>
          )}
          <div>
            <h2 className="text-xl font-semibold text-white">
              {transaction.senderFirstName} {transaction.senderLastName}
            </h2>
            <p className="text-sm text-white/60">
              Transaction ID: {transaction.transactionId}
            </p>
          </div>
        </div>
      </div>

      {/* Transaction Details */}
      <div className="bg-white/10 dark:bg-white/5 backdrop-blur-md rounded-2xl shadow border border-white/10 p-6 space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {transaction.accountNumber && (
            <Detail
              label="Account Number"
              value={`****${transaction.accountNumber.slice(-4)}`}
            />
          )}
          {transaction.network && (
            <Detail label="Network" value={transaction.network} />
          )}
          {transaction.transactionType && (
            <Detail
              label="Transaction Type"
              value={transaction.transactionType}
            />
          )}
          {transaction.transactionMethod && (
            <Detail label="Method" value={transaction.transactionMethod} />
          )}
          {transaction.currency && (
            <Detail label="Currency" value={transaction.currency} />
          )}
          <Detail
            label="Amount"
            value={`${transaction.currency} ${Number(
              transaction.amount
            ).toLocaleString()}`}
          />
          <Detail
            label="Fee"
            value={`${transaction.currency} ${Number(
              transaction.fee
            ).toLocaleString()}`}
          />
          {transaction.tx_ref && (
            <Detail
              label="Reference"
              value={
                !showTxRef ? (
                  <button
                    onClick={() => setShowTxRef(true)}
                    className="text-xs text-white/60 hover:text-white underline flex items-center gap-1"
                  >
                    <Eye className="w-4 h-4" /> View Transaction Reference
                  </button>
                ) : (
                  <button
                    onClick={() => setShowTxRef(false)}
                    type="button"
                    className="text-xs text-white/80 flex items-center gap-1"
                  >
                    <EyeOff className="w-4 h-4" /> {transaction?.tx_ref}
                  </button>
                )
              }
            />
          )}
          {!isProofUrl && transaction.summary && (
            <Detail
              label="Summary"
              value={(() => {
                let parsed;

                // Try to parse as JSON
                if (typeof transaction.summary === 'string') {
                  try {
                    parsed = JSON.parse(transaction.summary);
                  } catch {
                    parsed = transaction.summary;
                  }
                } else {
                  parsed = transaction.summary;
                }

                // Render parsed object
                if (typeof parsed === 'object' && parsed !== null) {
                  return (
                    <pre className="text-sm text-white/80 whitespace-pre-wrap break-words">
                      {JSON.stringify(parsed, null, 2)}
                    </pre>
                  );
                }

                // If it's a URL
                if (typeof parsed === 'string' && /^https?:\/\//.test(parsed)) {
                  return (
                    <a
                      href={parsed}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center text-blue-400 hover:underline"
                    >
                      Open Link <ExternalLink className="ml-1 w-4 h-4" />
                    </a>
                  );
                }

                // Plain string fallback
                return parsed;
              })()}
            />
          )}

          {transaction.createdAt && (
            <Detail
              label="Created At"
              value={new Date(transaction.createdAt).toLocaleString()}
            />
          )}
          {transaction.updatedAt && (
            <Detail
              label="Updated At"
              value={new Date(transaction.updatedAt).toLocaleString()}
            />
          )}
        </div>

        <div className="flex justify-between items-center pt-4 border-t border-white/10">
          <span className="text-white/70 font-medium">Status:</span>
          <span
            className={`text-sm px-4 py-1 rounded-full font-semibold ${statusColor}`}
          >
            {transaction.status}
          </span>
        </div>

        {/* Proof preview */}
        {isProofUrl && (
          <div className="pt-4 flex flex-row items-start justify-between">
            <Button
              onClick={() => setShowProofModal(true)}
              variant="glass"
              className="w-1/2 flex items-start justify-start text-start"
            >
              <span>Proof of Transaction</span>
              <br />
              <span className="text-xs text-white/70">
                User uploads proof of transaction e.g Bank Receipts.{' '}
              </span>
            </Button>
          </div>
        )}
      </div>

      {/* Framer Motion Fullscreen Modal */}
      {showProofModal && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-2xl"
        >
          <div className="relative bg-white/10 dark:bg-white/5 border border-white/10 rounded-2xl shadow-lg max-w-5xl w-full mx-4 p-6">
            <button
              onClick={() => setShowProofModal(false)}
              className="absolute top-4 right-4 text-white bg-white/10 hover:bg-white/20 p-2 rounded-full"
            >
              <X className="w-5 h-5" />
            </button>
            <img
              src={proof}
              alt="Proof of Transaction"
              className="w-full max-h-[80vh] object-contain rounded-xl mb-4"
            />
            <div className="text-right">
              <a
                href={transaction.summary}
                download={`proof-${transaction.tx_ref || 'transaction'}`}
                className="inline-block px-5 py-2 rounded-lg bg-white/20 text-white hover:bg-white/30 transition"
              >
                Download File
              </a>
            </div>
          </div>
        </motion.div>
      )}
    </ContentLayout>
  );
};

const Detail = ({ label, value }) => {
  if (!value) return null;
  return (
    <div className="flex flex-col">
      <span className="text-sm text-white/60">{label}</span>
      <span className="text-base text-white font-medium truncate">{value}</span>
    </div>
  );
};
